import { useEffect, useMemo, useState } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useAnalyticsOrgLocations, DemoChip, LocationChip } from '@frontend/analytics-shared';
import { BarChartData, Chart, YAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { trackingIds } from '../../tracking-ids';
import { CompareLocationsButton } from '../compare-locations-button';
import {
  calcAverageIndustryPercentChange,
  getIndustryAverageComparisonText,
} from './helpers/industry-average-comparison-text';
import { getPracticeAnalyticsQueryVariables } from './helpers/query-variables';
import { usePaNavigate } from './hooks/use-pa-navigate';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { useMonthlyTrendChartModal } from './monthly-trend-chart-modal';
import { PracticeAnalyticsInfoTips } from './practice-analytics-info-tips';

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      hygieneReappointment {
        totals(start: $start, end: $end, step: $step) {
          hygieneReappointments {
            patients
            unscheduled
            rescheduledSameDay
            date
          }
          patients
          rescheduledSameDay
          unscheduled
        }
      }
      industryAvg {
        totals(start: $start, end: $end, step: $step) {
          hygieneFollowUpNotYetRescheduled
          hygieneFollowUpReappointmentSameDay
          hygieneFollowUpTotalVisits
        }
      }
    }
  }
`;

type IndustryAveragePercentChange = {
  notScheduled?: number;
  sameDayReappointments?: number;
  visits?: number;
};

type ChartData = {
  data: BarChartData;
  industryAveragePercentChange: IndustryAveragePercentChange;
  totalNotScheduled: number;
  totalSameDayReappointments: number;
  totalVisits: number;
};

const dataSequence = ['patients', 'unscheduled', 'rescheduledSameDay'];

export const HygieneFollowUpMetric = ({
  clickNoop,
  isDrillDownPage,
  onFetchStateChange,
}: PracticeAnalyticsTypes.MetricProps) => {
  const { t } = useTranslation('analytics');
  const alert = useAlert();
  const navigate = usePaNavigate();
  const { isSmallMode } = useIsSmallMode();
  const { demoData, filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'demoData',
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const { locationNames } = useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const [isLargeChart, setIsLargeChart] = useState<boolean>(false);

  const monthlyTrendModal = useMonthlyTrendChartModal({
    defaultKey: 'percentageSameDay',
    metric: 'hygieneReappointment',
    title: t('Trend: Hygiene Follow-Up'),
    trackingId: trackingIds.practiceAnalytics.hygieneFollowUpTrend,
  });

  const multipleLocationsSelected = (filters.locations?.length || 0) > 1;
  const showLocationComparison = isDrillDownPage && multipleLocationsSelected;

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(`hygieneFollowUpSummary-${isDemoAccount}-${JSON.stringify(filters)}`),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.HygieneFollowUpResponse>({
            locationIds: filters.locations,
            queries: [query],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.hygieneFollowUp : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const chartData: ChartData | null | undefined = useMemo(() => {
    const { aggregatedData, rawData } = PracticeAnalyticsAggregations.hygieneFollowUpSummary(data);

    if (!aggregatedData || !rawData) {
      return;
    }

    const { hygieneReappointment, industryAvg } = aggregatedData;

    const barChartData: BarChartData = showLocationComparison
      ? Object.entries(rawData).reduce(
          (acc, [locationId, locationDetails]) => {
            const { totals = {} } = locationDetails?.location.hygieneReappointment || {};

            acc.groups.push({
              name: locationNames[locationId] || locationId,
              values: (
                dataSequence as Array<Exclude<keyof PracticeAnalyticsTypes.HygieneReappointmentBreakdown, 'date'>>
              ).reduce((acc, key) => ({ ...acc, [key]: totals[key] || 0 }), {}),
            });
            return acc;
          },
          { groups: [] } as BarChartData
        )
      : hygieneReappointment?.totals?.hygieneReappointments?.reduce(
          (acc, { date, ...rest }) => {
            acc.groups.push({
              name: formatters.date(date, true),
              values: (
                dataSequence as Array<Exclude<keyof PracticeAnalyticsTypes.HygieneReappointmentBreakdown, 'date'>>
              ).reduce((acc, key) => ({ ...acc, [key]: rest[key] }), {}),
            });
            return acc;
          },
          { groups: [] } as BarChartData
        ) || [];

    return {
      data: barChartData,
      industryAveragePercentChange: {
        notScheduled: calcAverageIndustryPercentChange({
          industryAverage: industryAvg?.totals.hygieneFollowUpNotYetRescheduled,
          practiceValue: hygieneReappointment?.totals?.unscheduled,
        }),
        sameDayReappointments: calcAverageIndustryPercentChange({
          industryAverage: industryAvg?.totals.hygieneFollowUpReappointmentSameDay,
          practiceValue: hygieneReappointment?.totals?.rescheduledSameDay,
        }),
        visits: calcAverageIndustryPercentChange({
          industryAverage: industryAvg?.totals.hygieneFollowUpTotalVisits,
          practiceValue: hygieneReappointment?.totals?.patients,
        }),
      },
      totalNotScheduled: hygieneReappointment?.totals?.unscheduled || 0,
      totalSameDayReappointments: hygieneReappointment?.totals?.rescheduledSameDay || 0,
      totalVisits: hygieneReappointment?.totals?.patients || 0,
    };
  }, [data, locationNames, showLocationComparison]);

  useEffect(() => {
    onFetchStateChange?.(isLoading);
  }, [isLoading, onFetchStateChange]);

  return (
    <>
      <Chart
        colors={{
          patients: theme.colors.secondary.seaweed40,
          rescheduledSameDay: theme.colors.secondary.seaweed10,
          unscheduled: theme.colors.warning50,
        }}
        isLoading={isLoading}
        labels={{
          patients: t('Hygiene Visits'),
          rescheduledSameDay: t('Reappointment Same Day'),
          unscheduled: t('Not Yet Rescheduled'),
        }}
        noBorder={!isDrillDownPage && isSmallMode}
        onClick={
          clickNoop
            ? undefined
            : () => {
                navigate({
                  to: '/analytics/practice/hygiene-follow-up',
                });
              }
        }
        style={!isDrillDownPage && isSmallMode ? { marginBottom: 0 } : undefined}
        trackingId={trackingIds.practiceAnalytics.hygieneFollowUpChart}
      >
        {isDrillDownPage ? (
          showDemoChipAndBanner && <DemoChip />
        ) : (
          <>
            {!isSmallMode && (
              <Chart.Header
                actions={[monthlyTrendModal.triggerProps]}
                bottomElement={
                  multipleLocationsSelected ? (
                    <CompareLocationsButton style={{ marginBottom: theme.spacing(1) }} />
                  ) : null
                }
                infoTip={<PracticeAnalyticsInfoTips tip='hygieneFollowUp' />}
                leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
                title={t('Hygiene Follow-Up')}
              />
            )}
            <Chart.SneakPeakBar
              data={[
                {
                  key: 'hygieneVisits',
                  name: t('Hygiene Visits'),
                  omitSubtext: multipleLocationsSelected,
                  subtext: getIndustryAverageComparisonText(chartData?.industryAveragePercentChange.visits),
                  value: formatters.value(chartData?.totalVisits || 0),
                },
                isSmallMode
                  ? null
                  : {
                      key: 'reappointmentSameDay',
                      name: t('Reappointment Same Day '),
                      omitSubtext: multipleLocationsSelected,
                      subtext: getIndustryAverageComparisonText(
                        chartData?.industryAveragePercentChange.sameDayReappointments
                      ),
                      value: formatters.value(chartData?.totalSameDayReappointments || 0),
                    },
                {
                  key: 'notYetRescheduled',
                  name: t('Not Yet Rescheduled'),
                  omitSubtext: multipleLocationsSelected,
                  subtext: getIndustryAverageComparisonText(chartData?.industryAveragePercentChange.notScheduled),
                  value: formatters.value(chartData?.totalNotScheduled || 0),
                },
              ]}
            />
          </>
        )}
        <Chart.BarChart
          appearance={{
            collectiveTooltip: true,
            showXAxis: true,
            height: 180,
            ...(showLocationComparison
              ? {
                  customTooltipTitle: ({ groupName }) => <LocationChip locationName={groupName} noTruncating />,
                  customYAxisTick: ({ labels, groupName, ...rest }) => {
                    return (
                      <YAxisLabelValueTick
                        {...rest}
                        label={labels?.[groupName] || groupName}
                        onChartSizeChange={(isLargeChart) => setIsLargeChart(isLargeChart)}
                        xTransitionLabel={-190}
                      />
                    );
                  },
                  groupsGap: 24,
                  layout: 'vertical',
                  margin: { right: 20, left: isLargeChart ? 140 : 40 },
                  mode: 'stacked',
                  showGridLines: true,
                  showYAxis: true,
                }
              : {}),
          }}
          data={chartData?.data}
        />
        <Chart.Legends
          formatValue={formatters.value}
          values={{
            unscheduled: chartData?.totalNotScheduled || 0,
            patients: chartData?.totalVisits || 0,
            rescheduledSameDay: chartData?.totalSameDayReappointments || 0,
          }}
        />
      </Chart>
      {monthlyTrendModal.modalRenderer()}
    </>
  );
};
